import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOSchema from '../components/SEOSchema';
import { 
  Phone, 
  MapPin, 
  Star, 
  CheckCircle2, 
  Sparkles,
  Car,
  Clock,
  Shield,
  Award,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  beforeAfterImages, 
  services, 
  whatsappLink, 
  serviceArea, 
  testimonials,
  benefits,
  logo,
  businessHours,
  phoneLink,
  phoneNumber
} from '../data/mockData';

const Auto = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  const handleWhatsAppClick = () => {
    window.open(whatsappLink, '_blank');
  };

  const handlePhoneClick = () => {
    // Appeler la fonction Google Ads de suivi de conversion
    if (window.gtag_report_conversion) {
      window.gtag_report_conversion(phoneLink);
    } else {
      // Fallback si gtag n'est pas chargé
      window.location.href = phoneLink;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <SEOSchema />
      {/* Top Info Bar */}
      <div className="bg-blue-600 text-white py-2 px-4">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center text-sm">
          <div className="font-semibold mb-1 md:mb-0">
            BeauClean - Nettoyage Auto Premium à Bordeaux
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 text-xs md:text-sm">
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {businessHours.weekdays.days}: {businessHours.weekdays.hours}
            </span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-blue-100">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src={logo} 
                alt="BeauClean Service Logo" 
                className="h-14 w-auto object-contain cursor-pointer"
                onClick={() => navigate('/')}
              />
              <div className="hidden md:flex gap-2">
                <Button
                  variant="ghost"
                  className="text-blue-600 border-b-2 border-blue-600"
                >
                  Nettoyage Auto
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/meubles')}
                  className="text-gray-600 hover:text-blue-600"
                >
                  Nettoyage Meubles
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handlePhoneClick}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Phone className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Appeler</span>
              </Button>
              <Button 
                onClick={handleWhatsAppClick}
                className="bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">WhatsApp</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_car-detailing-pro-13/artifacts/kl3onxsg_IMG_5376.JPG')`,
          }}
        ></div>
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-blue-50/95"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 text-sm shadow-lg">
              <MapPin className="w-4 h-4 mr-2 inline" />
              {serviceArea.city} et {serviceArea.radius} autour
            </Badge>
            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight drop-shadow-sm">
              Redonnez Vie à Votre
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
                Intérieur de Voiture
              </span>
            </h2>
            <p className="text-xl text-gray-800 mb-10 max-w-2xl mx-auto leading-relaxed font-medium drop-shadow-sm">
              Service professionnel de nettoyage automobile à domicile. 
              Résultats spectaculaires garantis avec des produits de qualité premium.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={handlePhoneClick}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                Appeler Maintenant
              </Button>
              <Button 
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-6 shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Message WhatsApp
              </Button>
              <Button 
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 bg-white/80 text-lg px-8 py-6 transition-all duration-300 shadow-lg"
              >
                Voir les Tarifs
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.id} className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
                <CardContent className="pt-6 text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {benefit.id === 1 && <Car className="w-7 h-7 text-blue-600" />}
                    {benefit.id === 2 && <Award className="w-7 h-7 text-blue-600" />}
                    {benefit.id === 3 && <Clock className="w-7 h-7 text-blue-600" />}
                    {benefit.id === 4 && <Shield className="w-7 h-7 text-blue-600" />}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Nos Transformations</Badge>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Avant / Après
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez la qualité de notre travail à travers ces transformations spectaculaires
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {beforeAfterImages.map((image) => (
              <Card 
                key={image.id} 
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-blue-100 hover:border-blue-300 cursor-pointer group"
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative aspect-square overflow-hidden">
                  <img 
                    src={image.before} 
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="font-bold text-lg mb-1">{image.title}</h3>
                      <p className="text-sm text-white/90">{image.description}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Nos Forfaits</Badge>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Choisissez Votre Formule
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Des prestations adaptées à vos besoins avec des tarifs transparents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <Card 
                key={service.id}
                className={`relative overflow-hidden hover:shadow-2xl transition-all duration-300 ${
                  service.popular 
                    ? 'border-4 border-blue-600 transform lg:scale-105' 
                    : 'border-2 border-blue-100 hover:border-blue-300'
                }`}
              >
                {service.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 text-sm font-bold rounded-bl-lg">
                    POPULAIRE
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    {service.name}
                  </CardTitle>
                  <div className="text-5xl font-bold text-blue-600 mb-3">
                    {service.price}
                  </div>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Suppléments pour Premium */}
                  {service.supplements && service.supplements.length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-r">
                      <p className="text-xs font-semibold text-amber-800 mb-2">📋 Suppléments :</p>
                      <ul className="space-y-1.5">
                        {service.supplements.map((supp, idx) => (
                          <li key={idx} className="text-xs">
                            <span className="font-semibold text-amber-900">{supp.name}</span>
                            <span className="text-amber-700"> : {supp.price}</span>
                            <p className="text-xs text-amber-600 italic">{supp.description}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Note pour nettoyage extérieur */}
                  {service.note && (
                    <div className="mb-4 p-2.5 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-xs text-blue-700">💡 {service.note}</p>
                    </div>
                  )}
                  
                  {/* Badge Nouveau */}
                  {service.badge && !service.popular && (
                    <div className={`absolute top-0 right-0 ${service.badgeColor || 'bg-green-500'} text-white px-4 py-1.5 text-xs font-bold rounded-bl-lg`}>
                      {service.badge}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Button 
                      onClick={handlePhoneClick}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler
                    </Button>
                    <Button 
                      onClick={handleWhatsAppClick}
                      className={`w-full ${
                        service.popular 
                          ? 'bg-green-500 hover:bg-green-600' 
                          : 'bg-green-500 hover:bg-green-600'
                      } text-white shadow-lg hover:shadow-xl transition-all duration-300`}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Réserver
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Témoignages</Badge>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Ils Nous Font Confiance
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            Prêt à Transformer Votre Voiture ?
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-blue-100">
            Contactez-nous maintenant pour réserver votre prestation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handlePhoneClick}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-10 py-6 shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <Phone className="w-5 h-5 mr-2" />
              {phoneNumber}
            </Button>
            <Button 
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-green-500 text-white hover:bg-green-600 text-lg px-10 py-6 shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src={logo} 
                  alt="BeauClean Service Logo" 
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400">
                Service professionnel de nettoyage à domicile à Bordeaux et ses environs.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-lg">Horaires d'Ouverture</h4>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">{businessHours.weekdays.days}</p>
                    <p>{businessHours.weekdays.hours}</p>
                    <p className="text-red-400 font-semibold mt-2">{businessHours.closed}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-lg">Contact</h4>
              <div className="space-y-3">
                <Button 
                  onClick={handlePhoneClick}
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white hover:text-gray-900 transition-all duration-300"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {phoneNumber}
                </Button>
                <Button 
                  onClick={handleWhatsAppClick}
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white hover:text-gray-900 transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <div className="flex justify-center gap-6 mb-4">
              <button 
                onClick={() => navigate('/')}
                className="hover:text-white transition-colors"
              >
                Accueil
              </button>
              <button 
                onClick={() => navigate('/meubles')}
                className="hover:text-white transition-colors"
              >
                Meubles
              </button>
              <button 
                onClick={() => navigate('/blog')}
                className="hover:text-white transition-colors"
              >
                Blog
              </button>
            </div>
            <p>&copy; 2024 BeauClean. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 z-50 animate-pulse hover:animate-none"
        aria-label="Contact WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default Auto;
